/* ==========================================================================
   Solova — Core JavaScript
   ========================================================================== */

(function () {
  'use strict';

  /* ---------- Utility helpers ---------- */
  const Solova = window.Solova || {};

  Solova.formatMoney = function (cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const value = parseInt(cents, 10);
    const formatString = format || Solova.money_format || '${{amount}}';

    function defaultTo(value, defaultValue) {
      return value == null || value !== value ? defaultValue : value;
    }

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultTo(precision, 2);
      thousands = defaultTo(thousands, ',');
      decimal = defaultTo(decimal, '.');

      if (isNaN(number) || number == null) return '0';

      const fixedNumber = (number / 100.0).toFixed(precision);
      const parts = fixedNumber.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const cents = parts[1] ? decimal + parts[1] : '';

      return dollars + cents;
    }

    let formattedValue = '';

    switch (formatString.match(/\{\{\s*(\w+)\s*\}\}/)[1]) {
      case 'amount':
        formattedValue = formatWithDelimiters(value, 2);
        break;
      case 'amount_no_decimals':
        formattedValue = formatWithDelimiters(value, 0);
        break;
      case 'amount_with_comma_separator':
        formattedValue = formatWithDelimiters(value, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        formattedValue = formatWithDelimiters(value, 0, '.', ',');
        break;
    }

    return formatString.replace(/\{\{\s*(\w+)\s*\}\}/, formattedValue);
  };

  /* ---------- Cart API ---------- */
  Solova.cart = {
    async get() {
      const res = await fetch(Solova.routes.cart_url + '.js');
      return res.json();
    },

    async add(items) {
      const res = await fetch(Solova.routes.cart_add_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });
      return res.json();
    },

    async change(line, quantity) {
      const res = await fetch(Solova.routes.cart_change_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line, quantity }),
      });
      return res.json();
    },

    async update(updates) {
      const res = await fetch(Solova.routes.cart_update_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates }),
      });
      return res.json();
    },
  };

  /* ---------- Cart drawer ---------- */
  Solova.CartDrawer = class {
    constructor() {
      this.drawer = document.getElementById('cart-drawer');
      if (!this.drawer) return;

      this.closeBtn = this.drawer.querySelector('[data-cart-close]');
      this.itemsContainer = this.drawer.querySelector('[data-cart-items]');
      this.subtotalEl = this.drawer.querySelector('[data-cart-subtotal]');
      this.countEl = document.querySelectorAll('[data-cart-count]');

      this.closeBtn?.addEventListener('click', () => this.close());

      this.drawer.addEventListener('click', (e) => {
        if (e.target === this.drawer) this.close();
      });
      this.drawer.addEventListener('close', () => {
        document.body.classList.remove('overflow-hidden');
      });

      document.addEventListener('cart:open', () => this.open());
      document.addEventListener('cart:updated', (e) => this.render(e.detail));
    }

    open() {
      if (!this.drawer.open) this.drawer.showModal();
      document.body.classList.add('overflow-hidden');
    }

    close() {
      if (this.drawer.open) this.drawer.close();
    }

    render(cart) {
      if (this.subtotalEl) {
        this.subtotalEl.textContent = Solova.formatMoney(cart.total_price);
      }
      this.countEl.forEach((el) => {
        el.textContent = cart.item_count;
      });
    }
  };

  /* ---------- Intersection Observer animations ---------- */
  Solova.initAnimations = function () {
    const elements = document.querySelectorAll('[data-animate]');
    if (!elements.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    elements.forEach((el) => observer.observe(el));
  };

  /* ---------- Variant picker ---------- */
  Solova.VariantPicker = class {
    constructor(section) {
      this.section = section;
      this.picker = section.querySelector('[data-variant-picker]');
      if (!this.picker) return;

      const dataEl = section.querySelector('[data-product-json]');
      if (!dataEl) return;
      try {
        this.product = JSON.parse(dataEl.textContent);
      } catch (e) {
        return;
      }

      this.priceEl = section.querySelector('[data-product-price]');
      this.idInput = section.querySelector('input[name="id"]');
      this.addToCartBtn = section.querySelector('[data-add-to-cart]');
      this.gallery = section.querySelector('[data-product-gallery]');
      this.optionGroups = this.picker.querySelectorAll('[data-option-position]:not(button)');

      // Accept clicks on both text buttons (.product__option-btn) and
      // color swatches (.product__swatch)
      this.picker.addEventListener('click', (e) => {
        const btn = e.target.closest('.product__option-btn, .product__swatch');
        if (btn && this.picker.contains(btn)) this.onOptionClick(btn);
      });

      const params = new URLSearchParams(window.location.search);
      const variantParam = params.get('variant');
      if (variantParam) {
        const variant = this.product.variants.find((v) => String(v.id) === variantParam);
        if (variant) this.selectVariant(variant, { updateUrl: false });
      }
    }

    getCurrentSelection() {
      const selection = [];
      this.optionGroups.forEach((group) => {
        const position = parseInt(group.dataset.optionPosition, 10);
        const active = group.querySelector(
          '.product__option-btn.is-active, .product__swatch.is-active'
        );
        selection[position - 1] = active ? active.dataset.optionValue : null;
      });
      return selection;
    }

    findVariant(selection) {
      return this.product.variants.find((v) =>
        selection.every((val, idx) => v.options[idx] === val)
      );
    }

    onOptionClick(btn) {
      const group = btn.closest('.product__option');
      if (!group) return;
      group
        .querySelectorAll('.product__option-btn, .product__swatch')
        .forEach((b) => b.classList.remove('is-active'));
      btn.classList.add('is-active');

      const variant = this.findVariant(this.getCurrentSelection());
      if (variant) this.selectVariant(variant, { updateUrl: true });
    }

    selectVariant(variant, opts) {
      opts = opts || {};

      this.optionGroups.forEach((group) => {
        const position = parseInt(group.dataset.optionPosition, 10);
        const value = variant.options[position - 1];
        group
          .querySelectorAll('.product__option-btn, .product__swatch')
          .forEach((btn) => {
            btn.classList.toggle('is-active', btn.dataset.optionValue === value);
          });

        // Update the "Color: Ivory" / "Size: M" live label next to the option name
        const selectedLabel = group.querySelector(
          '[data-option-selected="' + position + '"]'
        );
        if (selectedLabel) selectedLabel.textContent = value;
      });

      if (this.idInput) this.idInput.value = variant.id;

      if (this.priceEl) {
        this.priceEl.textContent = Solova.formatMoney(variant.price);
      }

      if (this.addToCartBtn) {
        this.addToCartBtn.disabled = !variant.available;
        const addLabel = this.addToCartBtn.dataset.addToCartText || 'Add to cart';
        const soldOutLabel = this.addToCartBtn.dataset.soldOutText || 'Sold out';
        this.addToCartBtn.textContent = variant.available ? addLabel : soldOutLabel;
      }

      if (opts.updateUrl !== false) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }

      // Swap the gallery to the variant's featured media by toggling
      // .is-active on both the thumb and the main slide.
      if (this.gallery && variant.featured_media) {
        const mediaId = String(variant.featured_media.id);
        const slides = this.gallery.querySelectorAll('[data-product-slide]');
        const thumbs = this.gallery.querySelectorAll('[data-product-thumb]');

        // Find the slide index whose data-media-id matches
        let targetIndex = -1;
        slides.forEach((s, i) => {
          if (s.getAttribute('data-media-id') === mediaId) targetIndex = i;
        });

        if (targetIndex >= 0) {
          slides.forEach((s, i) => s.classList.toggle('is-active', i === targetIndex));
          thumbs.forEach((t, i) => t.classList.toggle('is-active', i === targetIndex));
        }
      }
    }
  };

  /* ---------- Mobile menu ---------- */
  Solova.MobileMenu = class {
    constructor() {
      this.menu = document.getElementById('mobile-menu');
      this.openBtn = document.querySelector('[data-menu-open]');
      this.closeBtn = document.querySelector('[data-menu-close]');

      if (!this.menu) return;

      this.openBtn?.addEventListener('click', () => this.open());
      this.closeBtn?.addEventListener('click', () => this.close());

      this.menu.addEventListener('click', (e) => {
        if (e.target === this.menu) this.close();
      });
      this.menu.addEventListener('close', () => {
        document.body.classList.remove('overflow-hidden');
      });
    }

    open() {
      if (this.menu.open) return;
      this.menu.showModal();
      // Force a reflow so the browser registers the pre-transition state,
      // then add the class that triggers the slide-in.
      this.menu.offsetHeight;
      this.menu.classList.add('is-open');
      document.body.classList.add('overflow-hidden');
    }

    close() {
      if (!this.menu.open) return;
      this.menu.classList.remove('is-open');
      var dialog = this.menu;
      dialog.addEventListener('transitionend', function handler() {
        dialog.removeEventListener('transitionend', handler);
        dialog.close();
      });
    }
  };

  /* ---------- Product card thumbnail preview ---------- */
  Solova.initCardThumbs = function () {
    document.querySelectorAll('[data-product-card]').forEach(function (card) {
      if (card.dataset.thumbsBound === 'true') return;
      card.dataset.thumbsBound = 'true';

      var slides = card.querySelectorAll('.product-card__image');
      var thumbs = card.querySelectorAll('[data-card-thumb]');
      var strip = card.querySelector('[data-card-thumbs]');
      if (slides.length === 0) return;

      function activate(index) {
        if (index < 0 || index >= slides.length) return;
        slides.forEach(function (s, i) { s.classList.toggle('is-active', i === index); });
        thumbs.forEach(function (t, i) { t.classList.toggle('is-active', i === index); });
      }

      // Default hover shows the second image (crossfade in) if one exists
      card.addEventListener('mouseenter', function () {
        if (slides.length > 1) activate(1);
      });

      // --- Drag-to-scroll on the thumb strip ---
      var dragState = { down: false, moved: false, startX: 0, startScroll: 0 };
      var DRAG_THRESHOLD = 4;

      if (strip) {
        strip.addEventListener('dragstart', function (e) { e.preventDefault(); });

        strip.addEventListener('mousedown', function (e) {
          dragState.down = true;
          dragState.moved = false;
          dragState.startX = e.pageX;
          dragState.startScroll = strip.scrollLeft;
          // Don't flip to is-dragging yet — wait until the user actually moves.
          // Otherwise pointer-events: none on thumbs kicks in during a plain
          // click and the resulting click lands on the strip, not the thumb.
          e.preventDefault();
        });

        strip.addEventListener('mousemove', function (e) {
          if (!dragState.down) return;
          var dx = e.pageX - dragState.startX;
          if (Math.abs(dx) > DRAG_THRESHOLD && !dragState.moved) {
            dragState.moved = true;
            strip.classList.add('is-dragging');
          }
          if (dragState.moved) {
            strip.scrollLeft = dragState.startScroll - dx;
          }
        });

        var endDrag = function () {
          if (!dragState.down) return;
          dragState.down = false;
          strip.classList.remove('is-dragging');
          // Clear 'moved' after the synthetic click has been processed
          setTimeout(function () { dragState.moved = false; }, 0);
        };

        strip.addEventListener('mouseup', endDrag);
        strip.addEventListener('mouseleave', endDrag);
      }

      // Single capture-phase handler on the card intercepts clicks that
      // originate inside the thumb strip or swatch strip — so they never
      // reach the <a> parent and never navigate to the product page.
      var swatchWrap = card.querySelector('[data-card-swatches]');
      var swatches = card.querySelectorAll('[data-card-swatch]');

      card.addEventListener('click', function (e) {
        // --- Thumb strip click ---
        if (strip && strip.contains(e.target)) {
          e.preventDefault();
          e.stopPropagation();

          if (dragState.moved) return;

          var thumb = e.target.closest('[data-card-thumb]');
          if (!thumb) return;

          var index = parseInt(thumb.dataset.cardThumb, 10);
          if (!isNaN(index)) activate(index);
          return;
        }

        // --- Color swatch click ---
        var swatch = e.target.closest('[data-card-swatch]');
        if (swatch && swatchWrap && swatchWrap.contains(swatch)) {
          e.preventDefault();
          e.stopPropagation();

          var mediaIndex = parseInt(swatch.dataset.cardSwatch, 10);
          if (!isNaN(mediaIndex) && mediaIndex >= 0) activate(mediaIndex);

          swatches.forEach(function (s) { s.classList.remove('is-active'); });
          swatch.classList.add('is-active');
        }
      }, true);

      // Leaving the card resets to the first image and first swatch
      card.addEventListener('mouseleave', function () {
        activate(0);
        swatches.forEach(function (s, i) { s.classList.toggle('is-active', i === 0); });
      });
    });
  };

  /* ---------- Init ---------- */
  document.addEventListener('DOMContentLoaded', function () {
    new Solova.CartDrawer();
    new Solova.MobileMenu();
    Solova.initAnimations();
    Solova.initCardThumbs();

    document.querySelectorAll('[data-product-section]').forEach((section) => {
      new Solova.VariantPicker(section);
    });
  });

  window.Solova = Solova;
})();
